import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";

const EMPTY = {
    name: "",
    description: "",
    card_limit: 100,
    price: 999,
    is_active: true,
};

export default function AdminPackages() {
    const [rows, setRows] = useState([]);
    const [edit, setEdit] = useState(null);

    const load = () =>
        api
            .get("/packages?active_only=false")
            .then(({ data }) => setRows(data));
    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        try {
            if (edit.id) await api.put(`/packages/${edit.id}`, edit);
            else await api.post("/packages", edit);
            toast.success("Saved");
            setEdit(null);
            load();
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) || err.message,
            );
        }
    };
    const remove = async (id) => {
        if (!confirm("Delete this package?")) return;
        await api.delete(`/packages/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div
            className="max-w-6xl mx-auto px-6 py-10"
            data-testid="admin-packages"
        >
            <div className="relative overflow-hidden mb-6 p-6 g-violet text-white shadow-glow-violet">
                <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
                <div className="relative flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-white/70">
                            Admin
                        </div>
                        <h1 className="font-display font-black text-4xl tracking-tighter">
                            Packages
                        </h1>
                    </div>
                    <button
                        className="bg-white text-violet-700 hover:bg-violet-50 px-5 py-2.5 font-semibold text-sm flex items-center gap-2"
                        onClick={() => setEdit({ ...EMPTY })}
                        data-testid="new-package-btn"
                    >
                        <Plus size={15} /> New Package
                    </button>
                </div>
            </div>

            <div className="card-surface overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-zinc-50 border-b">
                        <tr className="text-xs uppercase tracking-widest text-zinc-500">
                            <th className="text-left p-3">Name</th>
                            <th className="text-left p-3">Card Limit</th>
                            <th className="text-left p-3">Price</th>
                            <th className="text-left p-3">Active</th>
                            <th className="text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((p) => (
                            <tr
                                key={p.id}
                                className="border-b border-zinc-100"
                                data-testid={`pkg-row-${p.id}`}
                            >
                                <td className="p-3">
                                    <div className="font-medium">{p.name}</div>
                                    <div className="text-xs text-zinc-500">
                                        {p.description}
                                    </div>
                                </td>
                                <td className="p-3 font-mono">
                                    {p.card_limit}
                                </td>
                                <td className="p-3 font-mono">
                                    ₹{Number(p.price ?? 0).toFixed(0)}
                                </td>
                                <td className="p-3">
                                    {p.is_active ? (
                                        <span className="text-green-600 text-xs font-bold">
                                            ACTIVE
                                        </span>
                                    ) : (
                                        <span className="text-zinc-500 text-xs">
                                            OFF
                                        </span>
                                    )}
                                </td>
                                <td className="p-3 text-right">
                                    <button
                                        className="btn-ghost"
                                        onClick={() => setEdit({ ...p })}
                                        data-testid={`pkg-edit-${p.id}`}
                                    >
                                        <PencilSimple size={14} />
                                    </button>
                                    <button
                                        className="btn-ghost text-red-600"
                                        onClick={() => remove(p.id)}
                                        data-testid={`pkg-del-${p.id}`}
                                    >
                                        <Trash size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {edit && (
                <div
                    className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4"
                    data-testid="package-modal"
                >
                    <div className="bg-white w-full max-w-md border border-zinc-300">
                        <div className="p-5 border-b bg-zinc-950 text-white flex items-center justify-between">
                            <div className="font-display font-bold text-lg">
                                {edit.id ? "Edit" : "New"} Package
                            </div>
                            <button onClick={() => setEdit(null)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="label-swiss">Name</label>
                                <input
                                    className="input-swiss"
                                    value={edit.name}
                                    onChange={(e) =>
                                        setEdit({
                                            ...edit,
                                            name: e.target.value,
                                        })
                                    }
                                    data-testid="pkg-name-input"
                                />
                            </div>
                            <div>
                                <label className="label-swiss">
                                    Description
                                </label>
                                <textarea
                                    className="input-swiss"
                                    rows={2}
                                    value={edit.description}
                                    onChange={(e) =>
                                        setEdit({
                                            ...edit,
                                            description: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label-swiss">
                                        Card limit
                                    </label>
                                    <input
                                        type="number"
                                        className="input-swiss font-mono"
                                        value={edit.card_limit}
                                        onChange={(e) =>
                                            setEdit({
                                                ...edit,
                                                card_limit: Number(
                                                    e.target.value,
                                                ),
                                            })
                                        }
                                    />
                                </div>
                                <div>
                                    <label className="label-swiss">
                                        Price (₹)
                                    </label>
                                    <input
                                        type="number"
                                        className="input-swiss font-mono"
                                        value={edit.price}
                                        onChange={(e) =>
                                            setEdit({
                                                ...edit,
                                                price: Number(e.target.value),
                                            })
                                        }
                                    />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={edit.is_active}
                                    onChange={(e) =>
                                        setEdit({
                                            ...edit,
                                            is_active: e.target.checked,
                                        })
                                    }
                                />
                                Active (visible to users)
                            </label>
                        </div>
                        <div className="p-5 border-t bg-zinc-50 flex justify-end gap-2">
                            <button
                                className="btn-secondary"
                                onClick={() => setEdit(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={save}
                                data-testid="pkg-save-btn"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
